import React, { useState, useCallback } from 'react';
import { Zap, AlertTriangle, TrendingUp, Target, ChevronDown, ChevronUp, Loader2, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';
import { getAuthHeader } from '../utils/auth';

const URGENCY_CONFIG = {
  high: { color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20', label: 'High Priority' },
  medium: { color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20', label: 'Medium' },
  low: { color: 'text-[#52525b]', bg: 'bg-[#18181b] border-[#27272a]', label: 'Low' }
};

function Section({ title, icon: Icon, iconColor = '#6366f1', children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[#27272a] rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-[#111114] hover:bg-[#18181b] transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: iconColor }} />
          <span className="text-sm font-medium text-white" style={{fontFamily:'Manrope,sans-serif'}}>{title}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-[#52525b]" /> : <ChevronDown className="w-4 h-4 text-[#52525b]" />}
      </button>
      {open && <div className="px-4 py-3 bg-[#09090B] space-y-2">{children}</div>}
    </div>
  );
}

export default function GoogleOptimizationPanel({ accountId, accountName, addToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchOptimization = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/google/optimize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ account_id: accountId })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to fetch optimization data');
      }
      const result = await res.json();
      setData(result);
    } catch (err) {
      setError(err.message);
      addToast?.({ type: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [accountId, addToast]);

  if (!data && !loading && !error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#6366f1]/10 flex items-center justify-center">
          <Zap className="w-6 h-6 text-[#6366f1]" />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium text-white" style={{fontFamily:'Manrope,sans-serif'}}>AI Optimization Analysis</p>
          <p className="text-xs text-[#71717a] mt-1">Get AI-powered recommendations for {accountName}</p>
        </div>
        <button
          onClick={fetchOptimization}
          className="flex items-center gap-2 px-4 py-2 bg-[#6366f1] hover:bg-[#5558e8] text-white text-sm rounded-xl transition-colors"
        >
          <Zap className="w-3.5 h-3.5" />
          Analyze Account
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="w-6 h-6 text-[#6366f1] animate-spin" />
        <p className="text-sm text-[#71717a]">Analyzing your campaigns with AI...</p>
        <p className="text-xs text-[#52525b]">This may take 10-15 seconds</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-3">
        <XCircle className="w-6 h-6 text-red-400" />
        <p className="text-sm text-[#71717a]">{error}</p>
        <button onClick={fetchOptimization} className="text-xs text-[#6366f1] hover:underline">Try again</button>
      </div>
    );
  }

  const s = data?.suggestions || {};
  const stats = data?.stats || {};

  return (
    <div className="space-y-3">
      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'CTR', value: `${stats.ctr}%`, color: stats.ctr >= 2 ? '#22c55e' : '#ef4444' },
          { label: 'Avg CPC', value: `₹${stats.cpc?.toFixed(0)}`, color: '#a1a1aa' },
          { label: 'Conv Rate', value: `${stats.conv_rate}%`, color: stats.conv_rate >= 2 ? '#22c55e' : '#ef4444' }
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-[#111114] border border-[#27272a] rounded-xl p-3 text-center">
            <p className="text-xs text-[#52525b] mb-1">{label}</p>
            <p className="text-sm font-mono font-bold" style={{ color }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Health Assessment */}
      {s.health_assessment && (
        <div className="bg-[#6366f1]/5 border border-[#6366f1]/20 rounded-xl px-4 py-3">
          <p className="text-xs text-[#71717a] leading-relaxed">{s.health_assessment}</p>
        </div>
      )}

      {/* Priority Actions */}
      {s.priority_actions?.length > 0 && (
        <Section title="Priority Actions" icon={AlertTriangle} iconColor="#f59e0b">
          {s.priority_actions.map((action, i) => {
            const cfg = URGENCY_CONFIG[action.urgency] || URGENCY_CONFIG.low;
            return (
              <div key={i} className={`border rounded-xl px-3 py-2.5 ${cfg.bg}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-sm font-medium text-white">{action.action}</p>
                  <span className={`text-[10px] ${cfg.color} whitespace-nowrap`}>{cfg.label}</span>
                </div>
                <p className="text-xs text-[#71717a] mb-1">{action.reason}</p>
                {action.expected_impact && (
                  <p className="text-xs text-[#22c55e]">Expected: {action.expected_impact}</p>
                )}
              </div>
            );
          })}
        </Section>
      )}

      {/* Why Campaigns Are Failing */}
      {s.why_failing?.length > 0 && (
        <Section title="Campaign Issues" icon={XCircle} iconColor="#ef4444">
          {s.why_failing.map((item, i) => (
            <div key={i} className="border border-red-500/15 bg-red-500/5 rounded-xl px-3 py-2.5">
              <p className="text-xs font-medium text-white mb-1">{item.campaign}</p>
              <p className="text-xs text-[#71717a] mb-1.5">{item.reason}</p>
              <div className="flex items-start gap-1.5">
                <CheckCircle2 className="w-3 h-3 text-[#22c55e] mt-0.5 flex-shrink-0" />
                <p className="text-xs text-[#22c55e]">{item.fix}</p>
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* Negative Keywords */}
      {s.negative_keywords?.length > 0 && (
        <Section title="Suggested Negative Keywords" icon={Target} iconColor="#6366f1" defaultOpen={false}>
          <div className="flex flex-wrap gap-1.5">
            {s.negative_keywords.map((kw, i) => (
              <span key={i} className="text-xs bg-[#18181b] border border-[#27272a] text-[#a1a1aa] px-2 py-1 rounded-lg">
                -{kw}
              </span>
            ))}
          </div>
          <p className="text-[10px] text-[#52525b] mt-2">Add these as exact/phrase match negatives in your campaigns</p>
        </Section>
      )}

      {/* Next Plan of Action */}
      {s.next_poa?.length > 0 && (
        <Section title="Next Plan of Action" icon={TrendingUp} iconColor="#22c55e">
          <ol className="space-y-2">
            {s.next_poa.map((step, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-[#22c55e]/10 border border-[#22c55e]/30 text-[#22c55e] text-[10px] flex items-center justify-center flex-shrink-0 mt-0.5 font-mono">
                  {i + 1}
                </span>
                <p className="text-xs text-[#a1a1aa] leading-relaxed">{step}</p>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {/* Bid Strategy */}
      {s.bid_strategy_recommendations && (
        <Section title="Bid Strategy" icon={Zap} iconColor="#f59e0b" defaultOpen={false}>
          <p className="text-xs text-[#a1a1aa] leading-relaxed">{s.bid_strategy_recommendations}</p>
        </Section>
      )}

      {/* Refresh Button */}
      <button
        onClick={fetchOptimization}
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 py-2.5 border border-[#27272a] hover:border-[#6366f1]/40 hover:bg-[#6366f1]/5 rounded-xl text-xs text-[#71717a] hover:text-white transition-colors"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        Refresh Analysis
      </button>

      <p className="text-[10px] text-[#3f3f46] text-center">
        Generated {data?.generated_at ? new Date(data.generated_at).toLocaleString() : 'just now'}
      </p>
    </div>
  );
}
